<template>
  <div>
    <div class="form-group">
      <label class="label-option">{{ $i18nPresenter.t('YouTube link') }}
        <span class="title-table-presentation-tooltip result-layout-tooltip">
          <b-button
            v-tooltip="$i18nPresenter.t('Paste the link of the YouTube video you want to insert into this slide')"
            class="tooltip-btn"
          />
          <i class="fas fa-question-circle" />
        </span>
      </label>
      <InputText
        :val="slideActive.youtubeLink"
        @interface="updateYoutubeLink"
        :place-holder="'E.g., https://www.youtube.com/watch?v=dEDcc0aCjaA'"
        spellcheck="false"
        :id-name="'image-caption-slide-edit-link'"
        :index="''"
        :limit="80"
      />
    </div>
    <div class="form-group" :key="key">
      <span class="label-option">
        <label>{{ $i18nPresenter.t('Caption') }}</label>
        <TextAlignEdit />

      </span>
      <InputText
        :key="key"
        :val="slideActive.title"
        @interface="updateCaption"
        :place-holder="$i18nPresenter.t('Caption for the video…')"
        spellcheck="false"
        :id-name="'image-caption-slide-edit'"
        :index="''"
        :limit="80"
        :inputId="`slide-title-${slideActive.id}`"
      />
    </div>
    <div class="form-group">
      <div class="setting-block">
        <div class="block-content">
          <div class="form-check form-check-inline">
            <div class="custom-control custom-checkbox">
              <input
                type="checkbox"
                class="custom-control-input"
                id="youtube-check-show-iframe"
                v-model="slideActive.showYouTubeIframe"
                @change="_updateShowIframe()"
              >
              <label
                class="custom-control-label pointer"
                for="youtube-check-show-iframe"
              >{{ $i18nPresenter.t('Also show on audience\'s smartphones') }}</label>
              <span class="pl-1 title-table-presentation-tooltip">
                <b-button
                  v-tooltip="showOnAudienceTooltip"
                  class="tooltip-btn"
                />
                <i class="fas fa-question-circle" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import Config from '@/config'
import Socket from '@/socket'
import Util from '@/util'
import InputText from '@/components/presentations/InputText'
import TextAlignEdit from '@/components/presentations/TextAlignEdit.vue'
import UndoRedoMixin from '@/mixins/undo-redo/UndoRedoMixin'

export default {
  name: 'YoutubeConfig',
  components: {
    InputText,
    TextAlignEdit,
  },
  mixins: [UndoRedoMixin],
  data() {
    return {
      key: null,
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.currentUser,
      slideActive: (state) => state.slideActive,
    }),
    showOnAudienceTooltip() {
      return this.$i18nPresenter.t('Please note that your audience will need to click the Play button on the video themselves')
    },
  },
  mounted() {
    this.registerHistory()
  },
  methods: {
    ...mapActions(['setSyncLoadingAction', 'updateSlideActiveAction']),
    updateYoutubeLink(data) {
      if (!data.value || data.value === '' || (data.value !== '' && !Util.isURL(data.value))) {
        this.updateSlide({
          youtubeLink: '',
        })
        return
      }
      data.value = Util.toYouTubeLink(data.value)
      const id = Util.getParamValue('v', data.value)
      if (id) {
        this.updateSlide({
          youtubeLink: data.value,
        })
      } else {
        const embedIndex = data.value.indexOf('/embed/')
        if (embedIndex !== -1) {
          this.updateSlide({
            youtubeLink: data.value,
          })
        }
      }
    },

    backupUpdateCaption(newData) {
      const oldData = {
        ...newData,
        value: this.slideActive.title,
      }

      if (oldData.value !== newData.value) {
        this.addHistoryUpdateTitleSlide(newData, oldData)
      }
    },

    addHistoryUpdateTitleSlide(newValue, oldValue) {
      this.addActionToHistory({
        type: 'UpdateTitleCaption',
        data: JSON.parse(JSON.stringify(oldValue)),
        newData: JSON.parse(JSON.stringify(newValue)),
        slideActiveId: this.slideActive.id,
      })
    },

    async restoreUpdateTitleCaption(data) {
      if (data.value === null) {
        data.value = ''
      }
      await this.updateCaption(data, false)
      this.key = (new Date()).getTime()
    },

    updateCaption(data, isBackup = true) {
      if (isBackup) {
        this.backupUpdateCaption(data)
      }
      this.updateSlide({
        title: data.value,
        titleHTML: data.value,
      })
    },

    _updateShowIframe() {
      this.updateSlide({
        showYouTubeIframe: this.$store.state.slideActive.showYouTubeIframe,
      })
      let gaText
      if (this.$store.state.slideActive.showYouTubeIframe) {
        gaText = 'shows'
      } else {
        gaText = 'hides'
      }
      Util.mixpanelTracking(
        'Slide',
        'Edit setting',
        `Presenter ${gaText} Google slides iframe`,
      )
    },

    updateSlide(payload) {
      this.setSyncLoadingAction(1)
      this.updateSlideActiveAction(payload)
      payload.presentationId = this.$store.state.presentation.id
      payload.accessCode = this.$store.state.presentation.uniqueAccessCode
      payload.socketId = Socket.getSocketId()
      const slideId = JSON.parse(
        JSON.stringify(this.$store.state.slideActive.id),
      )
      payload.presenting = this.$store.getters.isPresenting || this.$store.getters.isBackstage
      this.$http
        .put(Config.api.updateSlide + this.$store.state.slideActive.id, payload)
        .then(
          () => {
            const index = this.$store.state.presentation.slides.findIndex(
              (item) => {
                return item.id === slideId
              },
            )

            if (index === -1) return
            if (payload.title === '') {
              payload.title = ' '
            }
            if (payload.title) {
              // this.$store.state.presentation.slides[index].title =
              //   payload.title
              Util.mixpanelTracking(
                'Slide',
                'Update slide image caption',
                'Done Update slide image caption',
              )
            }
            if (payload.youtubeLink || payload.youtubeLink === '') {
              this.$store.state.presentation.slides[index].youtubeLink =
                payload.youtubeLink
              Util.mixpanelTracking(
                'Slide',
                'Update slide youtube link',
                'Done Update slide youtube link',
              )
            }
            this.setSyncLoadingAction(2)
          },
          () => {
            this.setSyncLoadingAction(3)
          },
        )
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.select-group {
  label {
    cursor: pointer;
  }

  input {
    cursor: pointer;
  }
}
</style>
